import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';

const API_URL = process.env.REACT_APP_API_URL || 'https://api.echomemoir.ai/api';

const GiftSuccess = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const code = searchParams.get('code');

  const [giftData, setGiftData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!code) {
      setLoading(false);
      return;
    }

    const fetchGift = async () => {
      try {
        const res = await fetch(`${API_URL}/billing/gift/${code}`);
        const data = await res.json();
        if (data.code) {
          setGiftData(data);
        }
      } catch (err) {
        console.error('Gift lookup error:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchGift();
  }, [code]);

  const handleCopy = () => {
    navigator.clipboard.writeText(code).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  if (loading) {
    return (
      <div style={{ minHeight: '100vh', background: '#08060e', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'rgba(255,255,255,0.5)' }}>
        Loading...
      </div>
    );
  }

  if (!code || !giftData) {
    return (
      <div style={{ minHeight: '100vh', background: '#08060e', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', color: '#fff', padding: '20px' }}>
        <div style={{ fontSize: '48px', marginBottom: '16px' }}>🎁</div>
        <h2 style={{ marginBottom: '8px' }}>Gift code not found</h2>
        <p style={{ color: 'rgba(255,255,255,0.5)', marginBottom: '24px' }}>The gift code may still be processing. Check your email for confirmation.</p>
        <button onClick={() => navigate('/')} style={{ padding: '12px 24px', borderRadius: '10px', border: 'none', background: 'rgba(139,92,246,0.2)', color: '#a78bfa', fontSize: '14px', cursor: 'pointer' }}>
          Go Home
        </button>
      </div>
    );
  }

  const planEmoji = giftData.tier === 'legacy_vault' ? '🏛️' : '🌳';

  return (
    <div style={{
      minHeight: '100vh',
      background: '#08060e',
      color: '#fff',
      padding: '60px 20px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center'
    }}>
      <div style={{ maxWidth: '480px', width: '100%', textAlign: 'center' }}>

        {/* Success icon */}
        <div style={{
          width: '80px', height: '80px', borderRadius: '50%',
          background: 'linear-gradient(135deg, rgba(34,197,94,0.2), rgba(16,185,129,0.1))',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          margin: '0 auto 24px', fontSize: '40px'
        }}>
          🎁
        </div>

        <h1 style={{ fontSize: '26px', fontWeight: '700', marginBottom: '8px' }}>
          Your gift is ready!
        </h1>
        <p style={{ color: 'rgba(255,255,255,0.5)', fontSize: '15px', lineHeight: '1.6', marginBottom: '32px' }}>
          Payment confirmed. {giftData.recipientName ? `Share this code with ${giftData.recipientName}` : 'Share this code with your loved one'} — they can redeem it at any time.
        </p>

        {/* Gift code card */}
        <div style={{
          background: 'rgba(251,191,36,0.06)',
          border: '2px dashed rgba(251,191,36,0.25)',
          borderRadius: '20px',
          padding: '32px 24px',
          marginBottom: '24px'
        }}>
          <p style={{ color: 'rgba(255,255,255,0.4)', fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.15em', marginBottom: '10px' }}>
            Gift Code
          </p>
          <p style={{
            color: '#fbbf24', fontSize: '32px', fontWeight: '700',
            letterSpacing: '4px', fontFamily: 'monospace',
            marginBottom: '14px'
          }}>
            {giftData.code}
          </p>
          <p style={{ color: 'rgba(255,255,255,0.4)', fontSize: '13px', marginBottom: '20px' }}>
            {planEmoji} {giftData.planName} — {giftData.durationMonths} months
          </p>

          <button
            onClick={handleCopy}
            style={{
              padding: '10px 24px', borderRadius: '10px',
              border: '1px solid rgba(251,191,36,0.3)',
              background: copied ? 'rgba(34,197,94,0.15)' : 'rgba(251,191,36,0.1)',
              color: copied ? '#4ade80' : '#fbbf24',
              fontSize: '14px', fontWeight: '500', cursor: 'pointer',
              transition: 'all 0.2s ease'
            }}
          >
            {copied ? '✓ Copied!' : '📋 Copy Code'}
          </button>
        </div>

        {/* Gift message preview */}
        {giftData.giftMessage && (
          <div style={{
            background: 'rgba(255,255,255,0.03)',
            borderLeft: '3px solid #fbbf24',
            borderRadius: '0 10px 10px 0',
            padding: '14px 18px',
            marginBottom: '24px',
            textAlign: 'left'
          }}>
            <p style={{ color: 'rgba(255,255,255,0.4)', fontSize: '11px', marginBottom: '6px' }}>Your message:</p>
            <p style={{ color: 'rgba(255,255,255,0.7)', fontSize: '14px', fontStyle: 'italic', lineHeight: '1.6', margin: 0 }}>
              "{giftData.giftMessage}"
            </p>
          </div>
        )}

        {/* Info */}
        <div style={{
          background: 'rgba(139,92,246,0.06)',
          border: '1px solid rgba(139,92,246,0.15)',
          borderRadius: '12px',
          padding: '16px',
          marginBottom: '28px',
          textAlign: 'left'
        }}>
          <p style={{ color: 'rgba(255,255,255,0.5)', fontSize: '13px', lineHeight: '1.7', margin: 0 }}>
            📧 A confirmation email with the gift code has been sent to you.
            {giftData.recipientName && ' We also sent a notification to the recipient.'}
            <br /><br />
            The recipient can redeem the code at <strong style={{ color: '#a78bfa' }}>echomemoir.ai/redeem</strong> after creating a free account.
          </p>
        </div>

        {/* Actions */}
        <div style={{ display: 'flex', gap: '12px', justifyContent: 'center' }}>
          <button
            onClick={() => navigate('/gift')}
            style={{
              padding: '12px 20px', borderRadius: '10px',
              border: '1px solid rgba(255,255,255,0.1)',
              background: 'transparent', color: 'rgba(255,255,255,0.5)',
              fontSize: '14px', cursor: 'pointer'
            }}
          >
            Buy Another
          </button>
          <button
            onClick={() => navigate('/')}
            style={{
              padding: '12px 20px', borderRadius: '10px', border: 'none',
              background: 'linear-gradient(135deg, #8b5cf6, #6d28d9)',
              color: '#fff', fontSize: '14px', fontWeight: '600', cursor: 'pointer'
            }}
          >
            Go to EchoMemoir
          </button>
        </div>
      </div>
    </div>
  );
};

export default GiftSuccess;
